/**
 * Shortcode Products Loader
 * Fetches product data by ID using Shopify Storefront API and renders product cards
 */

class ShortcodeProducts extends HTMLElement {
  constructor() {
    super();
    this.placeholders = [];
  }

  connectedCallback() {
    // Only initialize when DOM is ready and element is in viewport
    // This prevents unnecessary work if element is not visible
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        // Small delay to ensure all scripts are loaded
        requestAnimationFrame(() => this.init());
      });
    } else {
      // DOM is ready, use requestAnimationFrame for next paint cycle
      requestAnimationFrame(() => this.init());
    }
  }
  

  init() {
    // Find all shortcode product placeholders
    this.placeholders = document.querySelectorAll('.shortcode-product-placeholder[data-product-id]');
    
    if (this.placeholders.length === 0) {
      return;
    }

    // Store placeholders in an array before wrapping (since wrapping moves them)
    const placeholderArray = Array.from(this.placeholders);

    // Wrap placeholders in slider-element and motion-list
    this.wrapInSliderAndMotionList();

    // Load products in parallel
    const productPromises = placeholderArray.map(placeholder => {
      const productId = placeholder.getAttribute('data-product-id');
      const sectionId = placeholder.getAttribute('data-section-id') || 'article-shortcode';
      
      return this.fetchProductCard(productId, sectionId)
        .then(html => {
          // Check if placeholder still exists (might have been moved)
          if (!placeholder.parentElement) {
            return { placeholder, success: false, productId };
          }
          
          if (html) {
            // Create a temporary container to parse the HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            
            // The HTML should already be a .card element from section rendering
            // Motion-list expects .card elements as direct children
            const cardElement = tempDiv.querySelector('.card') || tempDiv.firstElementChild;
            
            if (cardElement) {
              // Replace placeholder with the card element directly
              placeholder.replaceWith(cardElement);
              
              // Initialize any custom elements in the inserted HTML
              this.initializeCustomElements(cardElement);
              
              return { placeholder, success: true, productId, cardElement };
            } else {
              // Product not found - remove placeholder and log
              console.log('Product not available - Product ID:', productId);
              placeholder.remove();
              return { placeholder, success: false, productId };
            }
          } else {
            // Product not found - remove placeholder and log
            console.log('Product not available - Product ID:', productId);
            placeholder.remove();
            return { placeholder, success: false, productId };
          }
        })
        .catch(error => {
          const productId = placeholder.getAttribute('data-product-id');
          console.log('Product not available - Product ID:', productId, error);
          // Remove placeholder if it still exists
          if (placeholder.parentElement) {
            placeholder.remove();
          }
          return { placeholder, success: false, productId };
        });
    });

    Promise.all(productPromises).then((results) => {
      // Filter out failed products
      const successfulResults = results.filter(r => r && r.success);
      
      // Clean up empty sliders (where all products failed)
      this.cleanupEmptySliders();
      
      // Initialize carousel and motion-list after all products are loaded
      this.initializeCarouselAndMotionList();
      
      // Dispatch event when all products are loaded
      document.dispatchEvent(new CustomEvent('shortcodeProducts:loaded', {
        detail: { loaded: successfulResults.length, total: results.length }
      }));
    });
  }

  /**
   * Fetch product card HTML using Shopify's section rendering API
   * This fetches the full product card with all features (badges, ratings, swatches, etc.)
   */
  async fetchProductCard(productId, sectionId) {
    if (!productId) {
      return null;
    }

    try {
      // Step 1: Use recommendations API to get product handle from ID
      // The recommendations API accepts product_id and returns product data including handle
      const recommendationsUrl = `/recommendations/products.json?product_id=${productId}&limit=1`;
      
      const response = await fetch(recommendationsUrl);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.products && data.products.length > 0) {
          const product = data.products[0];
          const productHandle = product.handle;
          
          if (productHandle) {
            // Step 2: Fetch full product card via section rendering API
            const sectionCard = await this.fetchProductCardFromSection(productHandle, productId);
            
            // If section rendering fails, fall back to basic rendering
            if (sectionCard) {
              return sectionCard;
            } else {
              // Fallback: render basic card with available data
              console.warn('Section rendering failed, using fallback for product:', productId);
              return this.renderBasicProductCard(product, productId);
            }
          }
        }
      }
      
      // If recommendations API fails or returns no products, return null
      return null;
      
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }

  /**
   * Fetch product card HTML from section rendering endpoint
   * This gets the full product card with all theme features
   * Uses search endpoint like recently-viewed does
   */
  async fetchProductCardFromSection(productHandle, productId) {
    try {
      // Use search endpoint with section rendering (like recently-viewed does)
      // Format: /search?section_id=section-name&type=product&q=id:product_id
      const sectionUrl = `/search?section_id=shortcode-product-card&type=product&q=id:${productId}`;
      
      const response = await fetch(sectionUrl);
      
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Shopify section rendering wraps content in a section container
        // Look for the section wrapper first
        const sectionWrapper = doc.querySelector('#shopify-section-shortcode-product-card, [id*="shopify-section-shortcode-product-card"]');
        
        if (sectionWrapper) {
          // Look for the product card inside the section wrapper
          const cardElement = sectionWrapper.querySelector('.card.product-card');
          
          if (cardElement) {
            return cardElement.outerHTML;
          }
          
          // Fallback: look for any card element in the section
          const fallbackCard = sectionWrapper.querySelector('.card');
          if (fallbackCard) {
            return fallbackCard.outerHTML;
          }
        }
        
        // If no section wrapper found, try searching the whole document
        const cardElement = doc.querySelector('.card.product-card');
        if (cardElement) {
          return cardElement.outerHTML;
        }
        
        // Last fallback: any card element
        const fallbackCard = doc.querySelector('.card');
        if (fallbackCard) {
          return fallbackCard.outerHTML;
        }
        
        // Debug: log what we got
        console.warn('Could not find product card in section response. HTML preview:', html.substring(0, 500));
      } else {
        console.warn('Section rendering failed:', response.status, sectionUrl);
      }
    } catch (error) {
      console.error('Error fetching product card from section:', error, 'Product ID:', productId);
    }
    
    return null;
  }


  /**
   * Initialize custom elements after inserting HTML
   */
  initializeCustomElements(element) {
    // Find and initialize any custom elements within the inserted HTML
    const customElements = element.querySelectorAll('[is], product-form, hover-button, lazy-image');
    
    customElements.forEach(el => {
      // Trigger any initialization that might be needed
      if (el.connectedCallback && typeof el.connectedCallback === 'function') {
        try {
          el.connectedCallback();
        } catch (e) {
          // Some elements might not have this method
        }
      }
    });
    
    // Dispatch event for any listeners
    element.dispatchEvent(new CustomEvent('shortcodeProduct:loaded', {
      bubbles: true
    }));
    
    // Also dispatch a global event
    document.dispatchEvent(new CustomEvent('shortcodeProduct:loaded', {
      detail: { element }
    }));
  }

  /**
   * Wrap shortcode placeholders in slider-element and motion-list containers
   * Desktop: 4 columns, Mobile: 1 column, Carousel enabled
   */
  wrapInSliderAndMotionList() {
    // Check if placeholders are already wrapped
    const firstPlaceholder = this.placeholders[0];
    let sliderElement = firstPlaceholder.closest('slider-element');
    
    if (!sliderElement) {
      // Group placeholders by their parent container
      const placeholderGroups = this.groupByParent();
      
      placeholderGroups.forEach(({ parent, placeholders: group }) => {
        // Create slider-element wrapper (for carousel functionality)
        sliderElement = document.createElement('slider-element');
        sliderElement.id = `Slider-shortcode-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        sliderElement.className = 'grid slider slider--desktop slider--tablet shortcode-slider';
        sliderElement.setAttribute('selector', '.card-grid>.card');
        
        // Create motion-list wrapper
        const motionList = document.createElement('motion-list');
        motionList.className = 'card-grid card-grid--4 mobile:card-grid--1 grid shortcode-products-grid';
        
        // Insert slider-element before first placeholder in group
        const firstInGroup = group[0];
        parent.insertBefore(sliderElement, firstInGroup);
        
        // Insert motion-list inside slider-element
        sliderElement.appendChild(motionList);
        
        // Move all placeholders in group into motion-list
        group.forEach(placeholder => {
          motionList.appendChild(placeholder);
        });
      });
    }
  }

  /**
   * Group placeholders by their parent container
   * This creates separate motion-lists for products in different parent containers
   */
  groupByParent() {
    const groups = new Map();
    
    Array.from(this.placeholders).forEach(placeholder => {
      const parent = placeholder.parentElement;
      const parentKey = parent; // Use the element itself as key
      
      if (!groups.has(parentKey)) {
        groups.set(parentKey, { parent, placeholders: [] });
      }
      
      groups.get(parentKey).placeholders.push(placeholder);
    });
    
    return Array.from(groups.values());
  }

  /**
   * Clean up empty sliders where all products failed to load
   */
  cleanupEmptySliders() {
    const sliderElements = document.querySelectorAll('.shortcode-slider');
    
    sliderElements.forEach(sliderElement => {
      const motionList = sliderElement.querySelector('.shortcode-products-grid');
      
      if (motionList) {
        // Check if motion-list only has placeholders (all products failed)
        const hasCards = motionList.querySelectorAll('.card:not(.shortcode-product-placeholder)').length > 0;
        const hasPlaceholders = motionList.querySelectorAll('.shortcode-product-placeholder').length > 0;
        
        // If no cards and only placeholders, remove the entire slider
        if (!hasCards && hasPlaceholders) {
          // Remove all remaining placeholders
          motionList.querySelectorAll('.shortcode-product-placeholder').forEach(placeholder => {
            placeholder.remove();
          });
          
          // Remove the entire slider if it's now empty
          if (motionList.children.length === 0) {
            sliderElement.remove();
          }
        }
      }
    });
  }

  /**
   * Match card-grid column count to loaded product count (2–4).
   * Minimum 2 columns so a lone card matches the slot width of multi-card rows.
   */
  setCardGridColumnCount(motionList, cardCount) {
    if (!motionList || cardCount < 1) {
      return;
    }

    const cols = Math.min(Math.max(cardCount, 2), 4);

    for (let n = 1; n <= 5; n += 1) {
      motionList.classList.remove(`card-grid--${n}`);
    }

    motionList.classList.add(`card-grid--${cols}`);
  }

  /**
   * Initialize motion-list animations after products load
   */
  initMotionList(motionList) {
    if (!motionList) {
      return;
    }

    const runLoad = () => {
      if (motionList.reload && typeof motionList.reload === 'function') {
        motionList.reload();
      } else if (motionList.load && typeof motionList.load === 'function') {
        motionList.load();
      }
    };

    if (motionList.load && typeof motionList.load === 'function') {
      runLoad();
    } else {
      setTimeout(runLoad, 100);
    }
  }

  /**
   * Initialize carousel and motion-list after products are loaded
   * Column count follows product count; carousel only when 2+ products
   */
  initializeCarouselAndMotionList() {
    const motionLists = document.querySelectorAll('.shortcode-products-grid');

    motionLists.forEach((motionList) => {
      const cardCount = motionList.querySelectorAll('.card:not(.shortcode-product-placeholder)').length;

      this.setCardGridColumnCount(motionList, cardCount);

      const sliderElement = motionList.closest('.shortcode-slider');

      if (!sliderElement) {
        this.initMotionList(motionList);
        return;
      }

      if (cardCount <= 1) {
        const parent = sliderElement.parentElement;
        if (parent) {
          parent.insertBefore(motionList, sliderElement);
          sliderElement.remove();
        } else {
          sliderElement.classList.remove('slider', 'slider--desktop', 'slider--tablet');
        }
      } else {
        sliderElement.classList.add('slider', 'slider--desktop', 'slider--tablet');

        const indicators = sliderElement.querySelectorAll('.indicators, [is="previous-button"], [is="next-button"]');
        indicators.forEach((indicator) => {
          indicator.style.display = 'none';
        });

        if (sliderElement.init && typeof sliderElement.init === 'function') {
          sliderElement.init();
        }
      }

      this.initMotionList(motionList);
    });
  }

  /**
   * Render a basic product card as fallback
   * This uses the product data from recommendations API to render a card
   */
  renderBasicProductCard(product, productId) {
    if (!product) {
      return null;
    }
    
    try {
      const productUrl = `/products/${product.handle}`;
      const image = product.featured_image || product.images?.[0];
      const price = product.variants?.[0]?.price;
      const compareAtPrice = product.variants?.[0]?.compare_at_price;
      const variantId = product.variants?.[0]?.id;
      
      return `
        <div class="card product-card product-card--default flex flex-col leading-none relative" data-product-id="${productId}">
          ${image ? `
            <div class="product-card__media relative h-auto">
              <a class="block relative media media--square overflow-hidden" href="${productUrl}">
                <img 
                  src="${image}" 
                  alt="${this.escapeHtml(product.title)}"
                  loading="lazy"
                  class="w-full h-full object-cover"
                  is="lazy-image"
                />
              </a>
            </div>
          ` : ''}
          <div class="product-card__content grow flex flex-col gap-4">
            <div class="product-card__details flex flex-col items-baseline gap-2 w-full">
              <p class="grow">
                <a class="product-card__title reversed-link text-base-xl font-medium" href="${productUrl}">
                  ${this.escapeHtml(product.title)}
                </a>
              </p>
              ${price ? `
                <div class="flex flex-wrap flex-row-reverse gap-2">
                  ${compareAtPrice && compareAtPrice > price ? `
                    <span class="text-sm line-through opacity-60">${this.formatMoney(compareAtPrice / 100)}</span>
                  ` : ''}
                  <span class="text-base font-medium">
                    ${this.formatMoney(price / 100)}
                  </span>
                </div>
              ` : ''}
            </div>
            ${variantId ? `
              <form action="/cart/add" method="post" enctype="multipart/form-data" class="product-form">
                <input type="hidden" name="id" value="${variantId}">
                <button type="submit" class="button button--primary button--fixed" is="hover-button">
                  <span class="btn-fill" data-fill></span>
                  <span class="btn-text">Add to cart</span>
                </button>
              </form>
            ` : `
              <a href="${productUrl}" class="button button--primary">
                View Product
              </a>
            `}
          </div>
        </div>
      `;
    } catch (error) {
      console.error('Error rendering basic product card:', error);
      return null;
    }
  }

  /**
   * Format money
   */
  formatMoney(cents) {
    const value = parseFloat(cents);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }

  /**
   * Escape HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

}

customElements.define('shortcode-products', ShortcodeProducts);

